import { Select } from 'antd'
import { useState, useRef, useEffect } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import RedInfoIcon from '../icons/RedInfoIcon'
import CloseIconOutlined from '../assets/icons/CloseIconOutlined'

interface IWizrTagInput {
  onChange?: any
  placeHolder?: string
  open?: boolean
  options?: any
  value?: any
  showSearch?: boolean
  maxTagCount?: any
  background?: string
  style?: any
  /* Optional popup overrides for local usage */
  getPopupContainerProp?: () => HTMLElement
  dropdownStyleProp?: React.CSSProperties
  dropdownMatchSelectWidth?: boolean
  errorMessage?: string
  showError?: boolean
  required?: boolean
}
const GlobalSelectStyles = createGlobalStyle`
  .ant-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background-color: #f0f0f0 !important;
    color: #000 !important;
  }
  /* Ensure dropdown overlays everything in the app (drawers, fixed panels, etc.) */
  .ant-select-dropdown {
    z-index: 12000 !important;
  }
`;

const StyledSelect = styled(Select) <any>`
width: 100%;
  .ant-select-selector {
    background-color: ${(p) => (p.background ?? '#F2F4F9')} !important;
    border: 1px solid ${(p) => (p.showError ? '#ff4d4f' : '#d9d9d9')} !important;
    border-radius: 16px !important;
    min-height: 52px !important;
    height: auto !important;
    padding-inline-end: 0 !important;
    padding: 8px 12px !important;
    display: flex !important;
    align-items: center !important;
    flex-wrap: wrap !important;
  }
  
  .ant-select-selection-item {
    background-color: white !important;
    border: 1px solid #D9D9D9 !important;
    border-radius: 16px !important;
    padding: 4px 8px 4px 12px !important;
    margin-right: 4px !important;
    margin-bottom: 0 !important;
    margin-top: 0 !important;
    height: auto !important;
    line-height: 1.5 !important;
    color: #000 !important;
    font-size: 14px !important;
    display: inline-flex !important;
    align-items: center !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }
  
  .ant-select-selection-item-remove {
    color: #000 !important;
    margin-left: 4px !important;
    padding: 0 !important;
    flex-shrink: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    
    .anticon {
      display: none !important;
    }
  }
  
  .ant-select-selection-placeholder {
    text-align: left;
    background-color: ${(p) => (p.background ?? '#F2F4F9')} !important;
  }
  
  .ant-select-selection-overflow {
    gap: 0 !important;
    margin: 0 !important;
    display: flex !important;
    flex-wrap: wrap !important;
    align-items: center !important;
  }
  
  .ant-select-selection-overflow-item {
    margin-right: 0 !important;
  }

  &:hover .ant-select-selector {
    border-color: ${(p) => (p.showError ? '#ff4d4f' : '#d9d9d9')} !important;
  }

  &.ant-select-focused .ant-select-selector,
  &.ant-select-open .ant-select-selector {
    border-color: ${(p) => (p.showError ? '#ff4d4f' : '#000')} !important;
    box-shadow: none !important;
  }
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  font-family: Montserrat;
  display: flex;
  align-items: center;
  gap: 6px;
`

const WizrTagInput = ({
  value,
  onChange,
  placeHolder = 'Enter tags',
  open,
  options,
  showSearch = false,
  maxTagCount,
  background,
  style,
  getPopupContainerProp,
  dropdownStyleProp,
  dropdownMatchSelectWidth,
  errorMessage,
  showError = false,
  required = false
}: IWizrTagInput) => {
  const [selectedTags, setSelectedTags] = useState([])

  const handleTagChange = (value: any) => {
    setSelectedTags(value)
    onChange(value)
  }

  useEffect(() => {
    setSelectedTags(value)
  }, [value])

  const selectRef = useRef<any>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault() // Prevent the default behavior of the Enter key
      selectRef.current?.focus() // Set focus back to the Select component
    }
  }

  const tagRender = (props: any) => {
    const { label, value, closable, onClose } = props
    return (
      <span className="ant-select-selection-item">
        <span className="ant-select-selection-item-content">{label}</span>
        {closable && (
          <span
            className="ant-select-selection-item-remove"
            onClick={onClose}
            role="button"
            aria-label="Remove"
          >
            <CloseIconOutlined color="#000" size="16" />
          </span>
        )}
      </span>
    )
  }

  return (
    <>
      <GlobalSelectStyles />
      <StyledSelect
        showSearch={showSearch}
        mode={'tags'}
        ref={selectRef}
        onKeyDown={handleKeyDown}
        placeholder={placeHolder}
        value={selectedTags}
        onChange={handleTagChange}
        defaultValue={selectedTags}
        open={open}
        options={options}
        size='large'
        tokenSeparators={[',']}
        suffixIcon={''}
        maxTagCount={maxTagCount}
        background={background}
        showError={showError}
        required={required}
        tagRender={tagRender}
        /* Prefer local override; fallback to body */
        getPopupContainer={getPopupContainerProp || (() => document.body)}
        dropdownStyle={dropdownStyleProp || { zIndex: 12000, background: '#FFF', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        style={style}
      />
      {showError && errorMessage && (
        <ErrorText>
          <RedInfoIcon />
          <span>{errorMessage}</span>
        </ErrorText>
      )}
    </>
  )
}

export default WizrTagInput
